import { ChartBinSize, ChartInfoKind } from '@frontend/chart/types';
import { ReportType } from '@frontend/report/types';
import { ComponentType, ReactNode } from 'react';

import { InsightEntityFilterKey, InsightFilterConfig } from '../insight-detail/insight-filter-config';

export type InsightTab = 'pudo' | 'vending' | 'lending' | 'nivaliz' | 'hardware';

export interface InsightDetailContentProps {
    accountId: string;
}

export interface InsightDefinition {
    id: string;
    tab: InsightTab;
    chartKey?: string;
    infoKind?: ChartInfoKind;
    title: ReactNode;
    subtitle?: ReactNode;
    /** Which endpoint params are editable on the detail page. */
    filters: InsightFilterConfig;
    /** Same visualization as the overview card, rendered full-page. */
    DetailContent: ComponentType<InsightDetailContentProps>;
    /** When set, insight detail can export this report type with the same effective filters. */
    reportType?: ReportType;
}

const withEntityFilters = (config: Omit<InsightFilterConfig, 'entityFilters'>, entityFilters: InsightEntityFilterKey[] = []): InsightFilterConfig => ({
    ...config,
    entityFilters
});

/** Range only; bin fixed (heatmaps, hour folds, aggregated full_period lists). */
export const lockedBinFilters = (
    defaultRange: InsightFilterConfig['defaultRange'],
    defaultBinSize: ChartBinSize,
    entityFilters: InsightEntityFilterKey[] = []
): InsightFilterConfig =>
    withEntityFilters(
        {
            enabled: true,
            showDateRange: true,
            defaultRange,
            editableBinSizes: [],
            defaultBinSize,
            lockBinSize: true
        },
        entityFilters
    );

/** Range + selectable bins with guards. */
export const editableBinFilters = (
    defaultRange: InsightFilterConfig['defaultRange'],
    defaultBinSize: ChartBinSize,
    editableBinSizes: ChartBinSize[],
    entityFilters: InsightEntityFilterKey[] = []
): InsightFilterConfig =>
    withEntityFilters(
        {
            enabled: true,
            showDateRange: true,
            defaultRange,
            editableBinSizes,
            defaultBinSize,
            lockBinSize: false
        },
        entityFilters
    );

/** Entity filters only (no date/bin bar) — e.g. stale lists, fleet snapshot. */
export const entityOnlyFilters = (entityFilters: InsightEntityFilterKey[]): InsightFilterConfig =>
    withEntityFilters(
        {
            enabled: true,
            showDateRange: false,
            defaultRange: 'none',
            editableBinSizes: [],
            defaultBinSize: ChartBinSize.FULL_PERIOD,
            lockBinSize: true
        },
        entityFilters
    );

export const noInsightFilters = (): InsightFilterConfig =>
    withEntityFilters({
        enabled: false,
        showDateRange: false,
        defaultRange: 'none',
        editableBinSizes: [],
        defaultBinSize: ChartBinSize.FULL_PERIOD,
        lockBinSize: true
    });
